import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    input,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ChangeDetectionStrategy,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SearchStore } from "../../stores/search.store";
import { S25HelpModule } from "../s25-help/s25.help.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { QLTerm } from "./s25ql.const";
import { S25qlSearchInputSuggestionsComponent } from "./s25ql.search.input.suggestions.component";
import { S25QLTokenizer } from "./s25ql.tokenizer";
import { Item } from "../../pojo/Item";
import { DropDownItem } from "../../pojo/DropDownItem";
import { S25Util } from "../../util/s25-util";
import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { DropdownApi } from "../s25-dropdown/dropdown.api";
import { S25QLSerializer } from "./s25ql.serializer";
import { S25QLSuggest } from "./s25ql.suggest";
import { SidebarStore } from "../s25-sidebar/s25.sidebar.store";
import { AiStore } from "../s25-chat-bot/s25.ai.store";
import { LoginStore } from "../../stores/login.store";
import { Search } from "./search";

@Component({
    selector: "s25-ng-ql-search-input",
    template: `
        <div class="searchWrapper">
            <div class="searchInputWrapper">
                <textarea
                    #searchInput
                    class="c-input searchInput"
                    [placeholder]="placeholder"
                    [attr.aria-label]="placeholder || 'Search'"
                    [(ngModel)]="value"
                    [disabled]="disabled"
                    (ngModelChange)="onInputChange($event)"
                    (click)="setTextareaHeight(); suggest()"
                    (keydown)="onKeydown($event)"
                    (keyup)="onKeyup($event)"
                    rows="1"
                ></textarea>
                @if (!disabled) {
                    <button class="clear" (click)="clearInput(); focusInput()" aria-label="Clear text">
                        <s25-ng-icon [type]="'close'" [ariaLabel]="false" [size]="16" />
                    </button>
                }
            </div>
            @if (loginStore.loggedIn() && !disabled) {
                <s25-ng-help-button [topic]="'series_ql'"></s25-ng-help-button>
            }
        </div>

        @if (suggestion) {
            <div class="suggestWrapper">
                <s25-ng-ql-search-input-suggestions
                    [type]="$any(type())"
                    [suggestion]="suggestion"
                ></s25-ng-ql-search-input-suggestions>
            </div>
        }

        @if (loginStore.loggedIn() && !disabled && hasAdvancedSearch()) {
            <div class="hint">
                <span class="cn-alert__icon cn-icon" name="alert--info">
                    <s25-ng-icon [type]="'info'" [ariaLabel]="false" [size]="'1.5em'" />

                    @if (!aiStore.isLoading() && aiStore.hasAiEnabled() && type() == 4 && !disableAi()) {
                        Hint! Type :: or use
                        <button class="c-textButton borderRight" (click)="sidebarStore.toggle()">
                            AI Assist
                            <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" [autoDarkMode]="autoDarkMode" />
                        </button>
                        for SeriesQL
                    } @else {
                        Hint! Type :: to use SeriesQL
                    }
                </span>
            </div>
        }
    `,
    styles: `
        .searchWrapper {
            display: flex;
            gap: 0.5em;
        }

        .searchInputWrapper {
            position: relative;
            flex-grow: 1;
        }

        textarea {
            display: block;
        }

        @property --scroll-height {
            syntax: "<length>";
            inherits: false;
            initial-value: 1px;
        }

        .searchInput {
            width: 100%;
            padding: 5px 2em 2px 0.75em;
            transition:
                color var(--transition-medium),
                border-color var(--transition-medium),
                box-shadow var(--transition-medium),
                background-color var(--transition-medium),
                --scroll-height var(--transition-medium);
            height: calc(max(var(--scroll-height), 2em) + 2px);
            resize: none;
        }

        .clear {
            position: absolute;
            right: 0;
            top: 0;
            border-radius: 50%;
            color: var(--color--text);
            background-color: transparent;
            border: 0;
            height: 1.5em;
            aspect-ratio: 1;
            cursor: pointer;
            margin: 0.25em;
            padding: 0;
        }

        .clear:active {
            background-color: #f0f1f2;
        }

        .clear:focus {
            box-shadow: 0 0 0 2px var(--color--link-text);
            color: var(--color--link-text);
            outline: 0;
        }

        .clear:hover {
            background-color: var(--color--surface-2-bg);
            color: var(--color--link-text);
        }

        s25-ng-help-button {
            color: var(--color--link-text);
            margin: auto 0;
        }

        .suggestWrapper {
            width: 24em;
            position: absolute;
        }

        .hint {
            font-size: 0.75em;
            padding: 0.5em 0;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, S25IconComponent, S25HelpModule, S25qlSearchInputSuggestionsComponent],
})
export class S25qlSearchInputComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() value: string = "";
    @Input() placeholder: string = "Search";
    @Input() hasHelpButton: boolean = true;
    readonly type = input.required<Search.TypeId>();
    /** @deprecated TODO: make ai search work in event form and occurrence modal */
    readonly disableAi = input(false);
    @Input() disabled: boolean = false;

    @Output() valueChange = new EventEmitter<string>();
    @Output() search = new EventEmitter<void>();
    @Output() errorMessage = new EventEmitter<string | undefined>();

    readonly loginStore = inject(LoginStore);
    readonly searchStore = inject(SearchStore);

    readonly hasAdvancedSearch = computed(() => this.searchStore.searchPermsByItemType()[this.type()].adv);

    @ViewChild("searchInput") inputElement: ElementRef;

    suggestion: {
        type: QLTerm.Suggest;
        data: {
            input: string;
            options?: DropDownItem[];
            objectType?: SearchCriteriaType["type"];
            chosenObjects?: DropDownItem[]; // For now s25-ng-dropdown-multi-search-criteria does not support picking chosen objects
            searchType?: Item.Ids;
        };
        inDropdown?: boolean; // To indicate whether the user used arrow keys to move into dropdown when suggesting a conjunction
        onSelect?: (...args: unknown[]) => void;
        rejectSuggestion: (reason?: any) => void;
    } | null;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private elementRef: ElementRef,
        protected sidebarStore: SidebarStore,
        protected aiStore: AiStore,
    ) {
        window.QLTokenizer = S25QLTokenizer;

        // Watch for type changes to update textarea height
        effect(() => {
            this.type(); // Track the signal

            this.setTextareaHeight();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.value) {
            this.setTextareaHeight();
        }
    }

    ngOnInit() {
        // Listen to "Escape" key in capturing phase because dropdowns stop propagation in bubbling phase
        // Reset suggestions when escape is pressed
        this.elementRef.nativeElement.addEventListener(
            "keydown",
            (event: KeyboardEvent) => event.key === "Escape" && this.resetSuggestion(true),
            true,
        );
        // fixes an issue on some machines where the textarea renders with the wrong height initially
        setTimeout(() => this.setTextareaHeight(), 1);
    }

    ngAfterViewInit() {
        this.setTextareaHeight();
    }

    onInputChange(value: string) {
        this.valueChange.emit(value);
        this.setTextareaHeight();
        this.suggest();
    }

    clearInput() {
        this.setValue("");
    }

    setValue(value: string) {
        this.value = value;
        this.valueChange.emit(value);
        this.setTextareaHeight();
        this.changeDetector.detectChanges();
    }

    focusInput() {
        this.inputElement?.nativeElement.focus();
    }

    setTextareaHeight() {
        const elem = this.inputElement?.nativeElement;
        if (!elem) return;
        // For unknown reasons this is needed when clearing the input
        elem.value = this.value;
        // ANG-7406: set the height of the element to tiny, and the browser will
        // immediately render it at minimum height. this means the scrollHeight
        // will reflect the actual text content. without this step, height will
        // only ratchet *up*, because scrollHeight *must* be >= element height.
        elem.style.setProperty("height", `1px`);
        elem.style.setProperty("--scroll-height", `${elem.scrollHeight}px`);
        elem.style.removeProperty("height");
    }

    async suggest(): Promise<void> {
        this.resetSuggestion();

        if (!this.hasAdvancedSearch()) return;
        if (!/^[\s\n\t]*::/.test(this.value)) return; // Don't do anything to pure queries

        const caretIndex = this.inputElement.nativeElement.selectionStart;
        if (caretIndex !== this.inputElement.nativeElement.selectionEnd) return; // User is selecting

        // If caret is before "::", then do not suggest
        if (!/^[\s\n\t]*::/.test(this.value.slice(0, caretIndex))) return;

        const { suggestion, promise, query } = S25QLSuggest.suggest(this.type(), this.value, caretIndex);
        this.suggestion = suggestion;
        this.changeDetector.markForCheck();
        if (!(await promise)) return; // No choice was made

        const serialized = S25QLSerializer.serialize(query);
        await this.setQuery(serialized.serial, serialized.caret);
        return this.suggest(); // Once we have completed a suggestion, see if we need to suggest the next entry
    }

    async setQuery(query: string, index?: number) {
        this.setValue(`::${query}`);
        this.inputElement.nativeElement.focus();
        await S25Util.delay(0); // Needed to let selectionStart update
        this.setTextareaHeight();
        this.inputElement.nativeElement.selectionStart = (index || query.length) + 2;
        this.inputElement.nativeElement.selectionEnd = (index || query.length) + 2;
        await S25Util.delay(0); // Needed to let selectionStart update
    }

    @HostListener("document:click")
    resetSuggestion(focus = false) {
        this.suggestion?.rejectSuggestion(); // Reject suggestion promise since it's never going to be resolved
        this.suggestion = null;
        this.changeDetector.markForCheck();
        if (focus) this.inputElement.nativeElement.focus();
    }

    onKeydown(event: KeyboardEvent) {
        switch (event.key) {
            case "ArrowUp":
            case "ArrowDown":
                // If we are currently suggesting and up or down arrow key is pressed, move selection rather than caret
                if (!this.suggestion) return;
                event.stopPropagation();
                event.preventDefault();
                return DropdownApi.arrow(this.elementRef.nativeElement, event.key === "ArrowUp" ? "up" : "down");
            case "Enter":
                if (event.shiftKey) return;
                event.preventDefault();
                event.stopPropagation();
                if (this.suggestion?.type && /^(option|objects)/.test(this.suggestion.type)) {
                    // If we are suggesting options and have not used arrow keys to move into dropdown, run search
                    if (!this.suggestion?.inDropdown) return this.runSearch();
                    // If we are suggesting options and have moved into dropdown, select the item
                    if (this.suggestion?.inDropdown) return DropdownApi.enterSelect(this.elementRef.nativeElement);
                } else return this.runSearch();
                return;
            case "Tab": {
                if (this.suggestion?.inDropdown && /^(option|objects)/.test(this.suggestion.type)) {
                    event.preventDefault();
                }
                return;
            }
            case "Escape":
                // If escape is pressed, reset suggestions
                return this.resetSuggestion(true);
        }

        this.clearErrorMessage();
    }

    onKeyup(event: KeyboardEvent) {
        // If we are currently suggesting and up or down arrow key is pressed, don't suggest
        if (this.suggestion && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
            if (event.key === "ArrowDown") this.suggestion.inDropdown = true; // Once you've moved into the suggestion
            return;
        }

        const noSuggest = /escape|enter|tab|shift|alt|meta|control/i.test(event.key);
        if (noSuggest) return;

        // Make suggestions while typing
        this.suggest();
    }

    runSearch() {
        this.resetSuggestion();
        this.search.emit();

        if (this.hasAdvancedSearch() && /^[\s\n\t]*::/.test(this.value)) {
            // Refresh input to reflect what was searched
            const tokens = S25QLTokenizer.safeTokenizer(this.value, this.type());
            if (!!tokens.error) return;
            const serialized = S25QLSerializer.serialize(tokens.value);
            this.setQuery(serialized.serial, serialized.caret);
        }
    }

    clearErrorMessage() {
        this.errorMessage.emit(undefined);
    }
}
